import { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Toolbar,
  IconButton,
  OutlinedInput,
  InputAdornment,
  Popover,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Badge,
  Tooltip,
  Divider,
  Stack,
  Button,
  Typography,
  TextField,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import MapIcon from '@mui/icons-material/Map';
import DnsIcon from '@mui/icons-material/Dns';
import AddIcon from '@mui/icons-material/Add';
import TuneIcon from '@mui/icons-material/Tune';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useManager } from '../common/util/permissions';
import { OP_STATUSES, getOperationalStatus } from './useFilter';

const STATUS_LABELS = {
  ON_MOVING: 'Em movimento',
  OFF: 'Ignição desligada',
  ON_STOPPED: 'Parado c/ motor ligado',
  IDLE_10M: 'Ocioso > 10 min',
  OUT_12H: 'Desconectado > 12h',
  OUT_24H: 'Desconectado > 24h',
  OUT_72H: 'Desconectado > 72h',
  NO_DATA: 'Sem dados',
};

const useStyles = makeStyles()((theme) => ({
  toolbar: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(0.75),
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0.75),
    },
  },

  leftActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },

  rightActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),
    minWidth: 0,
  },

  searchWrap: {
    minWidth: 0,
    width: '100%',
    maxWidth: 560,
    justifySelf: 'center',
  },

  searchInput: {
    width: '100%',
    background: theme.palette.background.paper,
    '& .MuiOutlinedInput-input': {
      minWidth: 0,
    },
    [theme.breakpoints.down('sm')]: {
      '& .MuiOutlinedInput-input': {
        paddingTop: 11,
        paddingBottom: 11,
      },
    },
  },

  toggleButton: {
    flexShrink: 0,
  },

  compactIconButton: {
    flexShrink: 0,
  },

  filterPanel: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    gap: theme.spacing(2),
    width: 'min(360px, calc(100vw - 20px))',
    maxWidth: '100%',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      width: 'calc(100vw - 16px)',
      padding: theme.spacing(1.5),
      gap: theme.spacing(1.5),
    },
  },

  filterHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

const MainToolbar = ({
  devicesOpen,
  setDevicesOpen,
  keyword,
  setKeyword,
  filter,
  setFilter,
  filterSort,
  setFilterSort,
  filterMap,
  setFilterMap,
  positions,
  onSearchFocus,
}) => {
  const { classes } = useStyles();
  const theme = useTheme();
  useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const navigate = useNavigate();
  const t = useTranslation();

  const manager = useManager();

  const groups = useSelector((state) => state.groups.items);
  const devices = useSelector((state) => state.devices.items);

  const inputRef = useRef(null);

  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  const deviceGroups = useMemo(() => (device) => {
    const groupIds = [];
    let { groupId } = device || {};

    while (groupId) {
      groupIds.push(groupId);
      groupId = groups[groupId]?.groupId || 0;
    }

    return groupIds;
  }, [groups]);

  const baseDevicesForCounters = useMemo(() => {
    const selectedGroups = filter?.groups || [];
    const lower = (keyword || '').toLowerCase();

    return Object.values(devices || {})
      .filter(Boolean)
      .filter((d) => {
        if (!selectedGroups.length) return true;
        return deviceGroups(d).some((id) => selectedGroups.includes(id));
      })
      .filter((d) => {
        if (!lower) return true;
        return [d.name, d.uniqueId, d.phone, d.model, d.contact]
          .some((s) => s && String(s).toLowerCase().includes(lower));
      });
  }, [devices, filter?.groups, keyword, deviceGroups]);

  const sortedGroupOptions = useMemo(
    () => Object.values(groups || {})
      .filter(Boolean)
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [groups],
  );

  const selectedGroupOptions = useMemo(() => {
    const selectedGroupIds = filter?.groups || [];
    if (!selectedGroupIds.length) return [];
    const selectedGroupIdSet = new Set(selectedGroupIds);
    return sortedGroupOptions.filter((group) => selectedGroupIdSet.has(group.id));
  }, [filter?.groups, sortedGroupOptions]);

  const groupOptionsToIds = (selectedOptions) => selectedOptions.map((group) => group.id);

  const deviceStatusCount = (status) => baseDevicesForCounters.filter((d) => {
    const position = positions?.[d.id];
    return getOperationalStatus(d, position) === status;
  }).length;

  const hasAnyFilter = Boolean(
    (filter?.statuses?.length || 0)
    + (filter?.groups?.length || 0)
    + (filterSort ? 1 : 0)
    + (filterMap ? 1 : 0),
  );

  const resetFilters = () => {
    setFilter({ statuses: [], groups: [] });
    setFilterSort('');
    setFilterMap(false);
  };

  const openFilter = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const closeFilter = () => {
    setFilterAnchorEl(null);
  };

  const searchPlaceholder = t('sharedSearchDevices');

  return (
    <Toolbar className={classes.toolbar}>
      <div className={classes.leftActions}>
        <Tooltip title={devicesOpen ? 'Mostrar mapa' : 'Mostrar lista'}>
          <IconButton
            edge="start"
            onClick={() => setDevicesOpen(!devicesOpen)}
            className={classes.toggleButton}
            aria-label={devicesOpen ? 'Mostrar mapa' : 'Mostrar lista de veículos'}
          >
            {devicesOpen ? <MapIcon /> : <DnsIcon />}
          </IconButton>
        </Tooltip>
      </div>

      <div className={classes.searchWrap}>
        <OutlinedInput
          inputProps={{ 'aria-label': 'Buscar veículo' }}
          ref={inputRef}
          className={classes.searchInput}
          placeholder={searchPlaceholder}
          value={keyword}
          onChange={(e) => {
            setKeyword(e.target.value);
          }}
          onFocus={() => {
            onSearchFocus?.();
          }}
          onClick={() => {
            onSearchFocus?.();
          }}
          startAdornment={(
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          )}
          endAdornment={(
            <InputAdornment position="end">
              <Tooltip title="Filtros">
                <IconButton
                  size="small"
                  edge="end"
                  onClick={openFilter}
                >
                  <Badge color="info" variant="dot" invisible={!hasAnyFilter}>
                    <TuneIcon fontSize="small" />
                  </Badge>
                </IconButton>
              </Tooltip>
            </InputAdornment>
          )}
          size="small"
          fullWidth
        />
      </div>

      <div className={classes.rightActions}>
        <Tooltip title="Adicionar dispositivo">
          <span>
            <IconButton
              edge="end"
              onClick={() => navigate('/settings/device')}
              disabled={!manager}
              className={classes.compactIconButton}
            >
              <AddIcon />
            </IconButton>
          </span>
        </Tooltip>
      </div>

      <Popover
        open={!!filterAnchorEl}
        anchorEl={filterAnchorEl}
        onClose={closeFilter}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        marginThreshold={8}
        slotProps={{
          paper: {
            style: {
              maxWidth: '100vw',
            },
          },
        }}
      >
        <div className={classes.filterPanel}>
          <div className={classes.filterHeader}>
            <Typography variant="subtitle2">
              Filtros
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                startIcon={<RestartAltIcon />}
                onClick={resetFilters}
              >
                Limpar
              </Button>
              <Button size="small" onClick={closeFilter}>
                Fechar
              </Button>
            </Stack>
          </div>

          <Divider />

          <FormControl fullWidth>
            <Autocomplete
              multiple
              options={OP_STATUSES}
              value={filter?.statuses || []}
              onChange={(_, selectedStatuses) => setFilter({ ...filter, statuses: selectedStatuses })}
              disableCloseOnSelect
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox checked={selected} sx={{ mr: 1 }} />
                  {`${STATUS_LABELS[option] || option} (${deviceStatusCount(option)})`}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Status"
                />
              )}
            />
          </FormControl>

          <FormControl fullWidth>
            <Autocomplete
              multiple
              options={sortedGroupOptions}
              value={selectedGroupOptions}
              onChange={(_, selectedOptions) => {
                setFilter({ ...filter, groups: groupOptionsToIds(selectedOptions) });
              }}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disableCloseOnSelect
              renderOption={(props, option, { selected, index }) => {
                const optionProps = { ...props };
                delete optionProps.key;
                const optionKey = option.id ?? `${option.name || 'group'}-${index}`;

                return (
                  <li key={optionKey} {...optionProps}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    {option.name || ''}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('settingsGroups')}
                  placeholder="Pesquisar grupo"
                />
              )}
            />
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>{t('sharedSortBy')}</InputLabel>
            <Select
              label={t('sharedSortBy')}
              value={filterSort}
              onChange={(e) => setFilterSort(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">{'\u00a0'}</MenuItem>
              <MenuItem value="name">{t('sharedName')}</MenuItem>
              <MenuItem value="lastUpdate">{t('deviceLastUpdate')}</MenuItem>
            </Select>
          </FormControl>

          <FormGroup>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={filterMap}
                  onChange={(e) => setFilterMap(e.target.checked)}
                />
              )}
              label={t('sharedFilterMap')}
            />
          </FormGroup>
        </div>
      </Popover>
    </Toolbar>
  );
};

export default MainToolbar;
